import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Radio,
  Scan,
  Cpu,
  BarChart3,
  Shield,
  Zap,
  Check,
  ChevronRight,
  Wifi,
  Activity,
  Crown,
  Building2,
  Menu,
  X,
  FileText,
  Bell,
  Smartphone,
  Send,
  Loader2,
  Play,
  ArrowRight,
  Users,
  Award,
  Clock,
  Mail,
  MapPin,
  Phone,
  Instagram,
  Linkedin,
  Youtube,
  MousePointer,
  Server,
  Globe,
  Monitor,
  Package,
  Signal,
  AlertTriangle,
  CheckCircle,
  Layers,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { LanguageSelector } from "@/components/LanguageSelector";
import { isFeatureAvailable, formatReleaseDate, RELEASE_DATE } from "@/lib/featureFlags";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Animated counter component
const AnimatedCounter = ({ end, duration = 2000, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [end, duration]);
  
  return <span>{prefix}{count}{suffix}</span>;
};

// Floating particles background
const FloatingParticles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(20)].map((_, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 bg-[#34C759]/30 rounded-full animate-pulse"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 3}s`,
          animationDuration: `${2 + Math.random() * 3}s`,
        }}
      />
    ))}
  </div>
);

export default function LandingPage({ onLogin }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    equipment_brand: "",
    equipment_model: "",
    message: "",
  });

  // Handle demo login
  const handleDemoLogin = async () => {
    setDemoLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/api/auth/demo`);
      const { access_token, user } = response.data;
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(user));
      if (onLogin) onLogin(user, access_token);
      toast.success("¡Bienvenido a la Demo de AVX RF PRO!");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Error al iniciar la demo. Intenta nuevamente.");
    } finally {
      setDemoLoading(false);
    }
  };

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % features.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.equipment_brand || !contactForm.equipment_model) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }
    
    setContactLoading(true);
    try {
      await axios.post(`${API_BASE}/api/contact/equipment-request`, contactForm);
      toast.success("¡Solicitud enviada! Nos pondremos en contacto pronto.");
      setShowContactModal(false);
      setContactForm({ name: "", email: "", equipment_brand: "", equipment_model: "", message: "" });
    } catch (error) {
      toast.error("Error al enviar la solicitud. Intenta nuevamente.");
    } finally {
      setContactLoading(false);
    }
  };

  const features = [
    {
      icon: Server,
      title: "Control de Red RF",
      description: "Descubre y controla equipos Shure y Sennheiser conectados a tu red. Cambia frecuencias remotamente y monitorea telemetría en tiempo real.",
      highlight: "Shure + Sennheiser",
      color: "#007AFF",
      badge: "NUEVO"
    },
    {
      icon: Activity,
      title: "Coordinación RF Inteligente",
      description: "Motor de análisis que detecta conflictos de spacing, intermodulación (IMD) y duplicados. Sugiere frecuencias alternativas automáticamente.",
      highlight: "Detección IMD 3er/5to orden",
      color: "#FFCC00",
      badge: "NUEVO"
    },
    {
      icon: Package,
      title: "Racks Virtuales",
      description: "Organiza tus sistemas en racks virtuales con slots duales (A/B). Arrastra y suelta para asignar. Analiza y envía frecuencias con un clic.",
      highlight: "Dual-channel slots",
      color: "#34C759",
    },
    {
      icon: Scan,
      title: "Escaneo RF USB",
      description: "Conecta tu Relacart DISCOVER o tinySA por USB para escaneos en tiempo real. Visualiza el espectro RF en vivo.",
      highlight: "WebHID + Web Serial",
      color: "#AF52DE",
    },
    {
      icon: BarChart3,
      title: "Modo Coordinación",
      description: "Analiza el espectro durante soundcheck o ensayo. Encuentra frecuencias limpias, detecta interferencias intermitentes y exporta reportes.",
      highlight: "Análisis profesional",
      color: "#34C759",
      badge: "NUEVO"
    },
    {
      icon: Signal,
      title: "Telemetría en Tiempo Real",
      description: "Monitorea nivel RF (dBm), nivel de audio, batería y frecuencia de cada canal. Indicadores visuales de estado.",
      highlight: "WebSocket streaming",
      color: "#FF9500",
    },
    {
      icon: Bell,
      title: "Alertas RF Inteligentes",
      description: "Detección automática de interferencias. Las frecuencias problemáticas se destacan en el espectro con colores de severidad.",
      highlight: "Alertas en tiempo real",
      color: "#FF3B30",
    },
  ];

  const plans = [
    {
      name: "Free",
      price: "Gratis",
      icon: Zap,
      color: "#71717A",
      features: [
        "6 sistemas IEM/Mic",
        "2 eventos",
        "1 rack",
        "Análisis spacing básico",
        "Sin control de red",
        "Sin escaneo USB",
        "Historial 7 días"
      ],
    },
    {
      name: "Pro",
      price: "$9.990",
      period: "CLP/mes",
      icon: Crown,
      color: "#34C759",
      popular: true,
      features: [
        "30 sistemas IEM/Mic",
        "20 eventos",
        "5 racks",
        "3 dispositivos de red",
        "Escaneo USB completo",
        "Análisis IMD 3er orden",
        "Push manual",
        "Reportes PDF",
        "Historial 90 días"
      ],
    },
    {
      name: "Enterprise",
      price: "$29.990",
      period: "CLP/mes",
      icon: Building2,
      color: "#FFCC00",
      features: [
        "Sistemas ilimitados",
        "Eventos ilimitados",
        "Racks ilimitados",
        "10 dispositivos de red",
        "Análisis IMD 3er+5to orden",
        "Push masivo",
        "5 usuarios",
        "Reportes + logo",
        "Soporte prioritario"
      ],
    },
    {
      name: "Enterprise Anual",
      price: "$199.990",
      period: "CLP/año",
      icon: Building2,
      color: "#AF52DE",
      badge: "AHORRA 44%",
      features: [
        "Todo de Enterprise",
        "Dispositivos ilimitados",
        "10 usuarios",
        "API Access",
        "Historial 2 años",
        "Capacitación 2hrs",
        "Soporte VIP 24/7"
      ],
    },
  ];

  const brands = [
    { name: "Shure", models: "PSM 1000, ULXD, Axient", color: "#E31837" },
    { name: "Sennheiser", models: "EW-D, EW-DX, 2000/6000", color: "#00A0E3" },
    { name: "Anleon", models: "S3D Series", color: "#34C759" },
    { name: "Audio-Technica", models: "M3, 3000 Series", color: "#F59E0B" },
    { name: "Relacart", models: "DISCOVER Scanner", color: "#8B5CF6" },
  ];

  const stats = [
    { value: 500, suffix: "+", label: "Eventos Gestionados" },
    { value: 50, suffix: "+", label: "Marcas Soportadas" },
    { value: 99.9, suffix: "%", label: "Uptime Garantizado" },
    { value: 24, suffix: "/7", label: "Soporte Técnico" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#34C759] to-[#28a745] rounded-xl flex items-center justify-center shadow-lg shadow-green-500/20">
                <Wifi className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white tracking-tight">AVX RF PRO</h1>
                <p className="text-[9px] text-[#34C759] font-medium tracking-widest">COORDINACIÓN RF PROFESIONAL</p>
              </div>
            </div>
            
            <div className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-sm text-gray-400 hover:text-white transition-colors">Funciones</a>
              <a href="#network" className="text-sm text-gray-400 hover:text-white transition-colors">Control de Red</a>
              <a href="#brands" className="text-sm text-gray-400 hover:text-white transition-colors">Equipos</a>
              <a href="#pricing" className="text-sm text-gray-400 hover:text-white transition-colors">Precios</a>
              <LanguageSelector />
              <Button
                onClick={handleDemoLogin}
                disabled={demoLoading}
                variant="outline"
                size="sm"
                className="border-[#34C759]/50 text-[#34C759] hover:bg-[#34C759]/10"
              >
                {demoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                Demo
              </Button>
              <Button
                onClick={() => navigate("/login")}
                size="sm"
                className="bg-[#34C759] text-black hover:bg-[#34C759]/90"
              >
                Ingresar
              </Button>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 text-gray-400"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#111111] border-t border-white/10 py-4">
            <div className="px-4 space-y-3">
              <a href="#features" className="block text-gray-400 hover:text-white py-2">Funciones</a>
              <a href="#network" className="block text-gray-400 hover:text-white py-2">Control de Red</a>
              <a href="#brands" className="block text-gray-400 hover:text-white py-2">Equipos</a>
              <a href="#pricing" className="block text-gray-400 hover:text-white py-2">Precios</a>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleDemoLogin} variant="outline" className="flex-1 border-[#34C759]/50 text-[#34C759]">
                  Demo
                </Button>
                <Button onClick={() => navigate("/login")} className="flex-1 bg-[#34C759] text-black">
                  Ingresar
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <FloatingParticles />
        
        {/* Gradient orbs */}
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#34C759]/20 rounded-full blur-[128px] pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#007AFF]/20 rounded-full blur-[128px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#007AFF]/10 border border-[#007AFF]/30 rounded-full mb-6">
              <Server className="w-4 h-4 text-[#007AFF]" />
              <span className="text-sm text-[#007AFF]">Nuevo: Control de Dispositivos RF en Red</span>
              <ChevronRight className="w-4 h-4 text-[#007AFF]" />
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 leading-tight">
              <span className="text-white">Coordinación RF</span>
              <br />
              <span className="bg-gradient-to-r from-[#34C759] via-[#00D4AA] to-[#007AFF] text-transparent bg-clip-text">
                Profesional
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
              La plataforma más avanzada para gestionar frecuencias RF, 
              controlar equipos en red y coordinar sistemas inalámbricos 
              <span className="text-[#34C759]"> Shure</span> y 
              <span className="text-[#00A0E3]"> Sennheiser</span>.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button
                onClick={handleDemoLogin}
                disabled={demoLoading}
                size="lg"
                className="bg-[#34C759] text-black hover:bg-[#34C759]/90 text-base px-8 h-12 shadow-lg shadow-green-500/25"
              >
                {demoLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Play className="w-5 h-5 mr-2" />
                )}
                Probar Demo Gratis
              </Button>
              <Button
                onClick={() => navigate("/register")}
                variant="outline"
                size="lg"
                className="border-white/20 text-white hover:bg-white/5 text-base px-8 h-12"
              >
                Crear Cuenta
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-white">
                    <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Network Control Section - NEW HIGHLIGHT */}
      <section id="network" className="py-20 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#007AFF]/5 to-transparent pointer-events-none" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <Badge className="bg-[#007AFF]/20 text-[#007AFF] mb-4">NUEVA FUNCIÓN</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Control de Dispositivos RF en Red
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Descubre, monitorea y controla tus equipos Shure y Sennheiser directamente desde la plataforma.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Feature list */}
            <div className="space-y-6">
              <div className="flex gap-4 p-4 bg-[#111111] border border-white/[0.08] rounded-lg">
                <div className="w-12 h-12 bg-[#E31837]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Signal className="w-6 h-6 text-[#E31837]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Descubrimiento Automático</h3>
                  <p className="text-sm text-gray-400">Escanea tu red local para encontrar equipos Shure (mDNS) y Sennheiser (UDP broadcast).</p>
                </div>
              </div>

              <div className="flex gap-4 p-4 bg-[#111111] border border-white/[0.08] rounded-lg">
                <div className="w-12 h-12 bg-[#00A0E3]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Monitor className="w-6 h-6 text-[#00A0E3]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Telemetría en Tiempo Real</h3>
                  <p className="text-sm text-gray-400">Monitorea RF level, audio, batería y frecuencia de cada canal con indicadores visuales.</p>
                </div>
              </div>

              <div className="flex gap-4 p-4 bg-[#111111] border border-white/[0.08] rounded-lg">
                <div className="w-12 h-12 bg-[#34C759]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 text-[#34C759]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Control Remoto de Frecuencias</h3>
                  <p className="text-sm text-gray-400">Cambia frecuencias desde la plataforma. Envía configuraciones desde Racks a equipos físicos.</p>
                </div>
              </div>

              <div className="flex gap-4 p-4 bg-[#111111] border border-white/[0.08] rounded-lg">
                <div className="w-12 h-12 bg-[#FFCC00]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Activity className="w-6 h-6 text-[#FFCC00]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Análisis de Coordinación</h3>
                  <p className="text-sm text-gray-400">Detecta conflictos de spacing (&lt;300kHz), intermodulación 3er/5to orden y duplicados.</p>
                </div>
              </div>
            </div>

            {/* Visual mockup */}
            <div className="relative">
              <div className="bg-[#111111] border border-white/[0.08] rounded-xl p-6 shadow-2xl">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#007AFF]/20 rounded-lg flex items-center justify-center">
                    <Server className="w-5 h-5 text-[#007AFF]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Dispositivos RF en Red</h4>
                    <p className="text-xs text-gray-500">2 dispositivos en línea</p>
                  </div>
                </div>

                {/* Device cards */}
                <div className="space-y-3">
                  <div className="p-3 bg-[#0A0A0A] rounded-lg border border-[#E31837]/30">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-[#34C759] rounded-full animate-pulse" />
                        <span className="text-sm font-medium text-white">Shure ULXD4Q</span>
                        <Badge className="bg-[#E31837]/20 text-[#E31837] text-[9px]">Shure</Badge>
                      </div>
                      <span className="text-xs text-gray-500">192.168.1.50</span>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {[1, 2, 3, 4].map(ch => (
                        <div key={ch} className="text-center p-2 bg-[#111111] rounded">
                          <div className="text-[10px] text-gray-500">CH {ch}</div>
                          <div className="text-xs font-mono text-[#34C759]">
                            {(600 + ch * 10).toFixed(3)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="p-3 bg-[#0A0A0A] rounded-lg border border-[#00A0E3]/30">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-[#34C759] rounded-full animate-pulse" />
                        <span className="text-sm font-medium text-white">Sennheiser EW-DX EM4</span>
                        <Badge className="bg-[#00A0E3]/20 text-[#00A0E3] text-[9px]">Sennheiser</Badge>
                      </div>
                      <span className="text-xs text-gray-500">192.168.1.51</span>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {[1, 2, 3, 4].map(ch => (
                        <div key={ch} className="text-center p-2 bg-[#111111] rounded">
                          <div className="text-[10px] text-gray-500">CH {ch}</div>
                          <div className="text-xs font-mono text-[#34C759]">
                            {(480 + ch * 8).toFixed(3)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Status bar */}
                <div className="mt-4 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1 text-[#34C759]">
                      <CheckCircle className="w-3 h-3" /> 8 canales activos
                    </span>
                  </div>
                  <span className="text-gray-500">Última actualización: ahora</span>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#007AFF]/20 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#34C759]/20 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Local Agent Section - NEW */}
      <section className="py-16 px-4 bg-gradient-to-b from-[#0A0A0A] to-[#111111]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Text content */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Badge className="bg-[#34C759]/20 text-[#34C759]">AGENTE LOCAL</Badge>
                <Badge className="bg-[#FFCC00]/20 text-[#FFCC00]">
                  <Clock className="w-3 h-3 inline mr-1" />
                  {formatReleaseDate()}
                </Badge>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Instalación en un clic
              </h2>
              <p className="text-gray-400 mb-6">
                El Agente Local conecta los equipos de tu red con AVX RF PRO en la nube. 
                Instalación automática, inicio con el sistema y sin configuración técnica.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#34C759]/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Download className="w-4 h-4 text-[#34C759]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white">Descarga e instala</h4>
                    <p className="text-sm text-gray-500">Un archivo, doble clic, listo. Compatible con Windows y macOS.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#007AFF]/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Zap className="w-4 h-4 text-[#007AFF]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white">Inicio automático</h4>
                    <p className="text-sm text-gray-500">Se ejecuta al encender tu computadora. Sin intervención manual.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#FFCC00]/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Globe className="w-4 h-4 text-[#FFCC00]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white">Acceso desde cualquier lugar</h4>
                    <p className="text-sm text-gray-500">Monitorea tus equipos desde la web, tablet o móvil sin estar en la red local.</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Visual */}
            <div className="relative">
              <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-xl p-6">
                {/* Terminal mockup */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-[#FF5F56]" />
                  <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
                  <div className="w-3 h-3 rounded-full bg-[#27C93F]" />
                  <span className="ml-2 text-xs text-gray-500">AVX RF Agent</span>
                </div>
                
                <div className="font-mono text-xs space-y-1 text-gray-400">
                  <p className="text-[#34C759]">========================================</p>
                  <p className="text-white">  AVX RF PRO - Agente Local v1.2</p>
                  <p className="text-[#34C759]">========================================</p>
                  <p className="mt-2">Servidor: <span className="text-[#007AFF]">avxrfpro.com</span></p>
                  <p>Subnet: <span className="text-white">192.168.1.*</span></p>
                  <p className="mt-2 text-[#34C759]">✓ Registrado exitosamente</p>
                  <p className="text-[#34C759]">✓ WebSocket conectado</p>
                  <p className="mt-2">Escaneando red...</p>
                  <p className="text-white">→ Encontrado: <span className="text-[#E31837]">Shure ULXD4Q</span> (192.168.1.50)</p>
                  <p className="text-white">→ Encontrado: <span className="text-[#00A0E3]">Sennheiser EW-DX</span> (192.168.1.51)</p>
                  <p className="mt-2 text-[#34C759]">2 dispositivos sincronizados con la nube</p>
                </div>
              </div>
              
              {/* Download badges */}
              <div className="flex gap-3 mt-4 justify-center">
                <div className="flex items-center gap-2 px-4 py-2 bg-[#111111] rounded-lg border border-white/10">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-[#00A4EF]" fill="currentColor">
                    <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
                  </svg>
                  <span className="text-sm text-white">Windows</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-[#111111] rounded-lg border border-white/10">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-[#A2AAAD]" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <span className="text-sm text-white">macOS</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Todo lo que necesitas para coordinación RF
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Herramientas profesionales para técnicos de audio que trabajan con sistemas inalámbricos.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index}
                className={`bg-[#111111] border-white/[0.08] hover:border-white/20 transition-all duration-300 cursor-pointer group ${
                  activeFeature === index ? 'ring-1 ring-offset-2 ring-offset-[#0A0A0A]' : ''
                }`}
                style={{ 
                  borderColor: activeFeature === index ? feature.color : undefined,
                  ringColor: activeFeature === index ? feature.color : undefined
                }}
                onClick={() => setActiveFeature(index)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${feature.color}20` }}
                    >
                      <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
                    </div>
                    {feature.badge && (
                      <Badge 
                        className="text-[9px]"
                        style={{ backgroundColor: `${feature.color}20`, color: feature.color }}
                      >
                        {feature.badge}
                      </Badge>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#34C759] transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-400 mb-3">{feature.description}</p>
                  <div className="flex items-center gap-1 text-xs" style={{ color: feature.color }}>
                    <Check className="w-3 h-3" />
                    {feature.highlight}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Brands */}
      <section id="brands" className="py-20 px-4 bg-[#111111]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Compatible con las mejores marcas
            </h2>
            <p className="text-gray-400">
              Soporte nativo para los principales fabricantes de equipos inalámbricos RF.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {brands.map((brand, index) => (
              <div 
                key={index}
                className="p-6 bg-[#0A0A0A] border border-white/[0.08] rounded-xl text-center hover:border-white/20 transition-all"
              >
                <h3 className="font-bold text-white mb-1" style={{ color: brand.color }}>
                  {brand.name}
                </h3>
                <p className="text-xs text-gray-500">{brand.models}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button
              onClick={() => setShowContactModal(true)}
              variant="outline"
              className="border-white/20 text-gray-400 hover:text-white"
            >
              ¿Tu equipo no está listado? Solicítalo
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Planes para cada necesidad
            </h2>
            <p className="text-gray-400">
              Desde freelancers hasta grandes productoras. Comienza gratis.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => (
              <Card 
                key={index}
                className={`bg-[#111111] border-white/[0.08] relative overflow-hidden ${
                  plan.popular ? 'ring-2 ring-[#34C759]' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-[#34C759] text-black text-[10px] font-bold px-3 py-1 rounded-bl-lg">
                    POPULAR
                  </div>
                )}
                {plan.badge && (
                  <div className="absolute top-0 right-0 bg-[#AF52DE] text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg">
                    {plan.badge}
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${plan.color}20` }}
                    >
                      <plan.icon className="w-5 h-5" style={{ color: plan.color }} />
                    </div>
                    <h3 className="font-bold text-white">{plan.name}</h3>
                  </div>
                  <div className="mb-6">
                    <span className="text-3xl font-bold text-white">{plan.price}</span>
                    {plan.period && <span className="text-gray-500 text-sm ml-1">{plan.period}</span>}
                  </div>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
                        <Check className="w-4 h-4 text-[#34C759] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => navigate("/register")}
                    className={`w-full ${
                      plan.popular 
                        ? 'bg-[#34C759] text-black hover:bg-[#34C759]/90' 
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    {plan.price === "Gratis" ? "Comenzar Gratis" : "Seleccionar Plan"}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#111111] to-[#0A0A0A]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            ¿Listo para coordinar como un profesional?
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Únete a cientos de técnicos de audio que ya usan AVX RF PRO para gestionar sus sistemas inalámbricos.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleDemoLogin}
              disabled={demoLoading}
              size="lg"
              className="bg-[#34C759] text-black hover:bg-[#34C759]/90 px-8"
            >
              {demoLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Play className="w-5 h-5 mr-2" />}
              Probar Demo Gratis
            </Button>
            <Button
              onClick={() => navigate("/register")}
              variant="outline"
              size="lg"
              className="border-white/20 text-white px-8"
            >
              Crear Cuenta
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-[#0A0A0A] border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-[#34C759] to-[#28a745] rounded-lg flex items-center justify-center">
                  <Wifi className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-white">AVX RF PRO</span>
              </div>
              <p className="text-sm text-gray-500">
                La plataforma más avanzada para coordinación de frecuencias RF y control de equipos inalámbricos.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Producto</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="#features" className="hover:text-white">Funciones</a></li>
                <li><a href="#network" className="hover:text-white">Control de Red</a></li>
                <li><a href="#pricing" className="hover:text-white">Precios</a></li>
                <li><a href="#brands" className="hover:text-white">Equipos Soportados</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Soporte</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="/manual" className="hover:text-white">Manual de Usuario</a></li>
                <li><a href="mailto:soporte@avxrfpro.com" className="hover:text-white">Contacto</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="/terms" className="hover:text-white">Términos de Servicio</a></li>
                <li><a href="/privacy" className="hover:text-white">Política de Privacidad</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/[0.06] pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} AVX RF PRO. Todos los derechos reservados.
            </p>
            <div className="flex items-center gap-4">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="bg-[#111111] border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Solicitar Soporte de Equipo</DialogTitle>
            <DialogDescription className="text-gray-400">
              Cuéntanos qué equipo quieres que agreguemos.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleContactSubmit} className="space-y-4">
            <div>
              <Label className="text-gray-400">Nombre</Label>
              <Input
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                className="bg-[#0A0A0A] border-white/10 text-white"
                placeholder="Tu nombre"
              />
            </div>
            <div>
              <Label className="text-gray-400">Email</Label>
              <Input
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                className="bg-[#0A0A0A] border-white/10 text-white"
                placeholder="tu@email.com"
              />
            </div>
            <div>
              <Label className="text-gray-400">Marca del Equipo</Label>
              <Input
                value={contactForm.equipment_brand}
                onChange={(e) => setContactForm({ ...contactForm, equipment_brand: e.target.value })}
                className="bg-[#0A0A0A] border-white/10 text-white"
                placeholder="Ej: Lectrosonics"
              />
            </div>
            <div>
              <Label className="text-gray-400">Modelo</Label>
              <Input
                value={contactForm.equipment_model}
                onChange={(e) => setContactForm({ ...contactForm, equipment_model: e.target.value })}
                className="bg-[#0A0A0A] border-white/10 text-white"
                placeholder="Ej: DSQD"
              />
            </div>
            <div>
              <Label className="text-gray-400">Mensaje (opcional)</Label>
              <Textarea
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                className="bg-[#0A0A0A] border-white/10 text-white"
                placeholder="Detalles adicionales..."
                rows={3}
              />
            </div>
            <Button 
              type="submit" 
              disabled={contactLoading}
              className="w-full bg-[#34C759] text-black hover:bg-[#34C759]/90"
            >
              {contactLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar Solicitud
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
